const ENDPOINT = '/api/client-errors';
const FLUSH_INTERVAL_MS = 10_000;
const MAX_BATCH_SIZE = 20;

function reportFingerprint(report) {
  return [
    report.kind,
    report.message,
    report.context,
    report.source,
    report.stack?.slice(0, 300),
  ].join('|');
}

export function createReportQueue({
  maxQueued = 40,
  budget = 50,
  dedupeWindowMs = 60_000,
  now = () => Date.now(),
} = {}) {
  const entries = [];
  const seen = new Map();
  let remaining = budget;

  function enqueue(report) {
    if (!report || typeof report !== 'object' || remaining <= 0 || entries.length >= maxQueued) {
      return false;
    }
    const time = now();
    const fingerprint = reportFingerprint(report);
    const lastSeen = seen.get(fingerprint);
    if (lastSeen !== undefined && time - lastSeen < dedupeWindowMs) return false;
    for (const [key, timestamp] of seen) {
      if (time - timestamp >= dedupeWindowMs) seen.delete(key);
    }
    seen.set(fingerprint, time);
    entries.push({ ...report });
    remaining -= 1;
    return true;
  }

  function drain(limit = MAX_BATCH_SIZE) {
    return entries.splice(0, Math.max(0, limit));
  }

  return {
    enqueue,
    drain,
    size: () => entries.length,
    remaining: () => remaining,
  };
}

function reportingDisabled() {
  return (
    typeof window === 'undefined' ||
    (typeof navigator !== 'undefined' && navigator.webdriver) ||
    new URLSearchParams(window.location.search).has('noanalytics')
  );
}

function pathname(value) {
  if (!value) return null;
  try {
    return new URL(value, window.location.origin).pathname;
  } catch {
    return null;
  }
}

function errorDetails(value) {
  if (value instanceof Error) {
    return { message: value.message, stack: value.stack || null };
  }
  if (typeof value === 'string') return { message: value, stack: null };
  try {
    return { message: JSON.stringify(value) || 'Unknown rejection', stack: null };
  } catch {
    return { message: 'Unknown rejection', stack: null };
  }
}

let initialized = false;

export function initErrorReporter() {
  if (initialized || reportingDisabled()) return;
  initialized = true;
  const queue = createReportQueue();

  function add(report) {
    try {
      queue.enqueue({
        ...report,
        page: window.location.pathname,
        timestamp: new Date().toISOString(),
      });
    } catch {
      // Error reporting must never become a new application error.
    }
  }

  function flush({ useBeacon = false } = {}) {
    const errors = queue.drain(MAX_BATCH_SIZE);
    if (errors.length === 0) return;
    try {
      const body = JSON.stringify({ errors });
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
        return;
      }
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Fail silent. A reporting failure must not recurse into the queue.
    }
  }

  window.addEventListener('error', (event) => {
    const details = errorDetails(event.error || event.message);
    add({
      kind: 'error',
      message: details.message,
      stack: details.stack,
      source: pathname(event.filename),
      line: event.lineno || null,
      column: event.colno || null,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const details = errorDetails(event.reason);
    add({
      kind: 'unhandledrejection',
      message: details.message,
      stack: details.stack,
    });
  });

  window.addEventListener('oggi:client-error', (event) => {
    const detail = event instanceof CustomEvent ? event.detail || {} : {};
    add({
      kind: 'scene',
      message: errorDetails(detail.message).message,
      stack: typeof detail.stack === 'string' ? detail.stack : null,
      context: typeof detail.context === 'string' ? detail.context : null,
      category: typeof detail.category === 'string' ? detail.category : null,
    });
  });

  setInterval(flush, FLUSH_INTERVAL_MS);
  window.addEventListener('pagehide', () => flush({ useBeacon: true }));
}
